import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";

// Where a page stands with the read it issued.
export type MountReadStatus = "loading" | "loaded" | "failed";

type Read = (isCurrent: () => boolean) => Promise<unknown>;

// Issues `read` on mount, and again whenever `read` changes (another route id,
// another query), and reports how the read THIS mount issued went (#407).
//
// A page must not decide "failed" from an app-scoped error atom. The atom
// outlives the page and is cleared only when the next request starts, from an
// effect, after the first paint. So a page revisited after a failed load
// painted the failure before any request had been issued, and a failure
// loading one client painted on the next client's page. A page using this
// decides from its own read instead (the approach of LegalClasses, #300, and
// BrowseAllCategories, #401 R7).
//
// - An outcome belongs to the `read` that produced it: the first render after
//   `read` changes reports "loading", never the previous read's outcome.
// - Every read starts at "loading", so a Retry shows that it is working.
// - `readStatus` follows the NEWEST operation, read or side read (below): a
//   double-clicked Retry, or a refetch behind a slow read, must not let the
//   older answer win. An operation a newer one overtook is retired: its
//   `isCurrent()` turns false and its answer does not settle the status.
// - A reload whose `read` is no longer the page's is not issued (PR 369
//   review 3): a handler for client A finishing after the page moved to
//   client B still holds A's `reload`, and A's read would become the newest
//   request and take the page over from B's.
//
// - `read` is handed `isCurrent()`: true until the hook issues another read
//   (a reload, a new `read`) or the page runs a side read. A page checks it
//   before writing its own state from the answer, so an older answer landing
//   last never replaces a newer one.
// - `runSideRead(sideRead)` is for a page that also fetches the same thing
//   outside `read` (EditProductPage's refresh after an image change). The
//   side read takes a turn and is handed its own `isCurrent()`, retired like
//   a read's. If it overtook a read still in flight, it settles the status in
//   that read's place: "loaded" on success, "failed" on failure (PR 378
//   review 1). If it overtook nothing, the status is already settled and the
//   side read leaves it alone; a failure is the caller's to report. Like
//   `reload`, a side read whose `read` is no longer the page's is refused: it
//   takes no turn and runs nothing (PR 378 review 3), as the new read
//   delivers. It never rejects.
//
// `reload` re-issues the read and resolves whether it loaded (false when it
// was not issued). It never rejects: a failure is reported through
// `readStatus` (and that boolean), so a Retry button or a fire-and-forget
// refetch leaves no unhandled rejection.
export const useMountRead = (read: Read) => {
    const [outcome, setOutcome] = useState<{
        read: Read;
        status: MountReadStatus;
    } | null>(null);
    // One numbering for reads and side reads: whoever took the latest turn is
    // current.
    const latestTurn = useRef(0);
    // Whether `readStatus` still waits on an answer: set when a read starts,
    // cleared when the newest operation settles it.
    const statusPending = useRef(false);
    // Updated in the commit that renders a new `read`, before any effect or
    // later callback can call a reload.
    const currentRead = useRef(read);
    useLayoutEffect(() => {
        currentRead.current = read;
    }, [read]);

    const reload = useCallback((): Promise<boolean> => {
        if (read !== currentRead.current) {
            return Promise.resolve(false);
        }
        const turn = ++latestTurn.current;
        const isCurrent = () => turn === latestTurn.current;
        const publish = (status: MountReadStatus) => {
            setOutcome({ read, status });
        };
        const settle = (status: MountReadStatus) => {
            if (isCurrent()) {
                statusPending.current = false;
                publish(status);
            }
        };

        statusPending.current = true;
        publish("loading");
        return read(isCurrent).then(
            () => {
                settle("loaded");
                return true;
            },
            () => {
                settle("failed");
                return false;
            },
        );
    }, [read]);

    const runSideRead = useCallback(
        (sideRead: Read): Promise<void> => {
            if (read !== currentRead.current) {
                return Promise.resolve();
            }
            const turn = ++latestTurn.current;
            const isCurrent = () => turn === latestTurn.current;
            // Taken at the start: a side read answers for the status only if
            // it overtook a read that had not settled it.
            const settlesStatus = statusPending.current;
            const settle = (status: MountReadStatus) => {
                if (settlesStatus && isCurrent()) {
                    statusPending.current = false;
                    setOutcome({ read, status });
                }
            };

            return sideRead(isCurrent).then(
                () => settle("loaded"),
                () => settle("failed"),
            );
        },
        [read],
    );

    useEffect(() => {
        void reload();
    }, [reload]);

    // Named for the page, where `status` usually means something else (an
    // order's, a client's, an HTTP response's).
    const readStatus: MountReadStatus =
        outcome?.read === read ? outcome.status : "loading";

    return { readStatus, reload, runSideRead };
};
